import React, { useEffect, useState, useRef } from "react";
import SectionHeading from "./SectionHeading";
import axiosInstance from "../../../utils/axios";
import { FaArrowLeft, FaArrowRight } from "react-icons/fa";
// Swiper imports
import { Swiper, SwiperSlide } from "swiper/react";
import { Navigation } from "swiper/modules";
import "swiper/css";
import "swiper/css/navigation";

const CertificateCard = ({ item }) => {
  return (
    <div className="w-full flex flex-col gap-y-4 transition-transform duration-300 hover:scale-105 relative">
      <div className="w-full h-56 lg:h-64 p-1 rounded-sm border-8 border-gray-800 shadow-lg bg-white flex items-center justify-center">
        <img
          src={item?.image}
          alt="certificate"
          className="w-full h-full object-cover object-center rounded-sm"
        />
      </div>
      <p className="capitalize text-sm sm:text-base text-gray-700 leading-relaxed px-4 text-center">
        {item?.title}
      </p>
    </div>
  );
};

// Skeleton loader
const CertificateSkeleton = ({ index }) => {
  return (
    <div
      key={index}
      className="flex-shrink-0 w-64 sm:w-72 lg:w-80 flex flex-col gap-y-4 mx-auto animate-pulse"
    >
      <div className="w-full h-56 lg:h-64 bg-gray-300 rounded-sm"></div>
      <div className="h-4 bg-gray-300 rounded mx-10"></div>
    </div>
  );
};

const Certifications = () => {
  const [certificates, setCertificates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [slidesPerView, setSlidesPerView] = useState(1);
  const [totalSlides, setTotalSlides] = useState(0);

  const swiperRef = useRef(null);

  const fetchCertificates = async () => {
    try {
      const resp = await axiosInstance.get(`/certificate`);
      setCertificates(resp.data || []);
    } catch (error) {
      console.error("Error fetching certificates", error);
      // toast.error("Failed to load certificates");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCertificates();
  }, []);

  const handleSwiperInit = (swiper) => {
    swiperRef.current = swiper;
    updateSwiperState(swiper);
  };

  const handleSlideChange = (swiper) => {
    updateSwiperState(swiper);
  };

  const updateSwiperState = (swiper) => {
    // Get current slidesPerView from swiper breakpoints
    let spv = swiper.params.slidesPerView;

    // Handle responsive breakpoints
    if (swiper.currentBreakpoint && swiper.params.breakpoints) {
      const breakpointData =
        swiper.params.breakpoints[swiper.currentBreakpoint];
      if (breakpointData && breakpointData.slidesPerView) {
        spv = breakpointData.slidesPerView;
      }
    }

    setSlidesPerView(spv);
    setCurrentSlide(swiper.activeIndex);
    setTotalSlides(swiper.slides.length);
  };

  const handleBreakpoint = (swiper) => {
    updateSwiperState(swiper);
  };

  const goToPrevSlide = () => {
    if (swiperRef.current) {
      swiperRef.current.slidePrev();
    }
  };

  const goToNextSlide = () => {
    if (swiperRef.current) {
      swiperRef.current.slideNext();
    }
  };

  const canGoPrev = currentSlide > 0;
  const canGoNext = currentSlide < totalSlides - slidesPerView;

  return (
    <div className="general-p-m flex flex-col justify-center mx-auto gap-10">
      <SectionHeading
        subtitle={"Certifications"}
        title={"Official Listings & Accreditations"}
      />

      {loading ? (
        <div className="flex gap-4 justify-center">
          {Array.from({ length: 4 }).map((_, index) => (
            <CertificateSkeleton key={index} index={index} />
          ))}
        </div>
      ) : (
        <>
          <div className="relative px-4 pt-4">
            <Swiper
              modules={[Navigation]}
              spaceBetween={20}
              slidesPerView={1}
              breakpoints={{
                640: { slidesPerView: 2 },
                1024: { slidesPerView: 3 },
                1280: { slidesPerView: 4 },
              }}
              onSwiper={handleSwiperInit}
              onSlideChange={handleSlideChange}
              onBreakpoint={handleBreakpoint}
              className="w-full overflow-visible"
              navigation={false}
              centeredSlides={true}
            >
              {certificates.map((item, idx) => (
                <SwiperSlide key={idx} className="overflow-visible my-4">
                  <CertificateCard item={item} />
                </SwiperSlide>
              ))}
            </Swiper>

            {/* Navigation Buttons Below Center */}
            <div className="flex justify-center items-center space-x-6 mt-6">
              <button
                onClick={goToPrevSlide}
                disabled={!canGoPrev}
                className={`w-12 h-12 border-2 rounded-full flex items-center justify-center transition-all duration-300 ${
                  !canGoPrev
                    ? "bg-gray-100 border-gray-200 cursor-not-allowed opacity-50"
                    : "bg-white border-gray-200 shadow-lg hover:shadow-xl hover:border-[#FCC101]"
                }`}
              >
                <FaArrowLeft className="text-gray-600" />
              </button>

              <button
                onClick={goToNextSlide}
                disabled={!canGoNext}
                className={`w-12 h-12 border-2 rounded-full flex items-center justify-center transition-all duration-300 ${
                  !canGoNext
                    ? "bg-gray-100 border-gray-200 cursor-not-allowed opacity-50"
                    : "bg-white border-gray-200 shadow-lg hover:shadow-xl hover:border-[#FCC101]"
                }`}
              >
                <FaArrowRight className="text-gray-600" />
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default Certifications;

// import React, { useEffect, useState } from "react";
// import SectionHeading from "./SectionHeading";
// import axiosInstance from "../../../utils/axios";

// const CertificateCard = ({ item }) => {
//   const [isHovered, setIsHovered] = useState(false);

//   return (
//     <div className="w-full">
//       {/* Main Certificate Container */}
//       <div
//         className="relative bg-gradient-to-br from-white via-gray-50 to-primaryFadedColor/5 rounded-xl sm:rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-500 overflow-hidden group border border-primaryFadedColor/20"
//         onMouseEnter={() => setIsHovered(true)}
//         onMouseLeave={() => setIsHovered(false)}
//       >
//         {/* Background Pattern */}
//         <div className="absolute inset-0 opacity-5">
//           <div className="absolute top-0 left-0 w-24 h-24 sm:w-32 sm:h-32 bg-primaryColor rounded-full -translate-x-12 -translate-y-12 sm:-translate-x-16 sm:-translate-y-16"></div>
//           <div className="absolute bottom-0 right-0 w-20 h-20 sm:w-24 sm:h-24 bg-accentColor rounded-full translate-x-10 translate-y-10 sm:translate-x-12 sm:translate-y-12"></div>
//           <div className="absolute top-1/2 right-0 w-12 h-12 sm:w-16 sm:h-16 bg-donatebuttonColor rounded-full translate-x-6 sm:translate-x-8"></div>
//         </div>

//         <div className="relative grid grid-cols-1 md:grid-cols-2 gap-6 sm:gap-8 p-6 sm:p-8 lg:p-12 items-center">
//           {/* Left Side - Certificate Image */}
//           <div className="relative group w-full">
//             <div className="relative w-full">
//               {/* Certificate Frame */}
//               <div
//                 className={`
//                 relative bg-white p-3 sm:p-4 rounded-xl shadow-lg border-2 transition-all duration-500 w-full
//                 ${
//                   isHovered
//                     ? "border-primaryColor shadow-xl md:transform md:rotate-1"
//                     : "border-gray-200 shadow-md"
//                 }
//               `}
//               >
//                 <div className="w-full aspect-[4/3] rounded-lg overflow-hidden bg-gray-50">
//                   <img
//                     src={item?.image}
//                     alt="certificate"
//                     className="w-full h-full object-contain transition-transform duration-500 group-hover:scale-105"
//                   />
//                 </div>

//                 {/* Certificate Badge */}
//                 <div className="absolute -top-2 -right-2 sm:-top-3 sm:-right-3 bg-gradient-to-r from-primaryColor to-donatebuttonColor text-white p-1.5 sm:p-2 rounded-full shadow-lg">
//                   <svg
//                     className="w-5 h-5 sm:w-6 sm:h-6"
//                     fill="none"
//                     stroke="currentColor"
//                     viewBox="0 0 24 24"
//                   >
//                     <path
//                       strokeLinecap="round"
//                       strokeLinejoin="round"
//                       strokeWidth={2}
//                       d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
//                     />
//                   </svg>
//                 </div>
//               </div>

//               {/* Floating Elements */}
//               <div className="absolute -bottom-3 -left-3 sm:-bottom-4 sm:-left-4 w-6 h-6 sm:w-8 sm:h-8 bg-accentColor/20 rounded-full animate-pulse"></div>
//               <div
//                 className="absolute -top-3 -right-6 sm:-top-4 sm:-right-8 w-5 h-5 sm:w-6 sm:h-6 bg-primaryColor/20 rounded-full animate-pulse"
//                 style={{ animationDelay: "0.5s" }}
//               ></div>
//             </div>
//           </div>

//           {/* Right Side - Certificate Details */}
//           <div className="space-y-4 sm:space-y-6 w-full min-w-0">
//             <div className="space-y-2 sm:space-y-3">
//               <h3 className="text-lg sm:text-2xl lg:text-3xl font-bold text-primaryTextColor leading-tight break-words">
//                 {item?.title}
//               </h3>
//               <div className="w-16 sm:w-20 lg:w-24 h-1.5 bg-gradient-to-r from-primaryColor to-accentColor rounded-full"></div>
//             </div>
//           </div>
//         </div>

//         {/* Animated Border on Hover */}
//         <div
//           className={`
//           absolute inset-0 rounded-xl sm:rounded-2xl transition-opacity duration-500 pointer-events-none
//           ${isHovered ? "opacity-100" : "opacity-0"}
//           bg-gradient-to-r from-transparent via-primaryColor/5 to-transparent
//         `}
//         ></div>
//       </div>
//     </div>
//   );
// };

// // Enhanced Skeleton loader
// const CertificateSkeleton = () => {
//   return (
//     <div className="w-full">
//       <div className="bg-white rounded-xl sm:rounded-2xl shadow-lg p-6 sm:p-8 lg:p-12 animate-pulse">
//         <div className="grid grid-cols-1 md:grid-cols-2 gap-6 sm:gap-8 items-center">
//           {/* Left Side Skeleton */}
//           <div className="w-full">
//             <div className="w-full aspect-[4/3] bg-skeletonLoaderColor rounded-xl"></div>
//           </div>

//           {/* Right Side Skeleton */}
//           <div className="space-y-4 sm:space-y-6 w-full min-w-0">
//             <div className="w-24 sm:w-32 h-5 sm:h-6 bg-skeletonLoaderColor rounded-full"></div>
//             <div className="space-y-3">
//               <div className="w-full max-w-xs h-6 sm:h-8 bg-skeletonLoaderColor rounded"></div>
//               <div className="w-16 sm:w-24 h-2 bg-skeletonLoaderColor rounded-full"></div>
//             </div>
//             <div className="space-y-2">
//               <div className="w-full h-4 bg-skeletonLoaderColor rounded"></div>
//               <div className="w-5/6 h-4 bg-skeletonLoaderColor rounded"></div>
//             </div>
//             <div className="grid grid-cols-2 gap-3">
//               {[...Array(4)].map((_, idx) => (
//                 <div
//                   key={idx}
//                   className="w-3/4 h-4 bg-skeletonLoaderColor rounded"
//                 ></div>
//               ))}
//             </div>
//             <div className="flex flex-wrap gap-3 sm:gap-4">
//               <div className="w-28 sm:w-32 h-10 sm:h-12 bg-skeletonLoaderColor rounded-lg"></div>
//               <div className="w-28 sm:w-32 h-10 sm:h-12 bg-skeletonLoaderColor rounded-lg"></div>
//             </div>
//           </div>
//         </div>
//       </div>

//       {/* Trust Indicators Skeleton */}
//       <div className="mt-8 sm:mt-12 grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4 sm:gap-6">
//         {[...Array(3)].map((_, idx) => (
//           <div
//             key={idx}
//             className="p-4 sm:p-6 bg-white/50 rounded-xl animate-pulse"
//           >
//             <div className="w-10 h-10 sm:w-12 sm:h-12 bg-skeletonLoaderColor rounded-full mx-auto mb-3"></div>
//             <div className="w-16 sm:w-20 h-3 sm:h-4 bg-skeletonLoaderColor rounded mx-auto mb-2"></div>
//             <div className="w-24 sm:w-32 h-2 sm:h-3 bg-skeletonLoaderColor rounded mx-auto"></div>
//           </div>
//         ))}
//       </div>
//     </div>
//   );
// };

// const Certifications = () => {
//   const [certificates, setCertificates] = useState([]);
//   const [loading, setLoading] = useState(true);

//   const fetchCertificates = async () => {
//     try {
//       const resp = await axiosInstance.get(`/certificate`);
//       setCertificates(resp.data || []);
//     } catch (error) {
//       console.error("Error fetching certificates", error);
//     } finally {
//       setLoading(false);
//     }
//   };

//   useEffect(() => {
//     fetchCertificates();
//   }, []);

//   const featuredCertificate = certificates.slice(0, 1); // only one

//   return (
//     <div className="w-full flex flex-col justify-center mx-auto gap-6 lg:gap-16 px-4 sm:px-6 lg:px-8 py-8 sm:py-12 lg:py-16">
//       <SectionHeading
//         subtitle="Certifications"
//         title="Official Listings & Accreditations"
//       />

//       <div className="w-full max-w-6xl mx-auto">
//         {loading ? (
//           <CertificateSkeleton />
//         ) : featuredCertificate.length > 0 ? (
//           <CertificateCard item={featuredCertificate[0]} />
//         ) : (
//           <div className="text-center py-12 sm:py-16 px-4">
//             <div className="max-w-md mx-auto">
//               <div className="w-20 h-20 sm:w-24 sm:h-24 bg-primaryFadedColor/20 rounded-full flex items-center justify-center mx-auto mb-4 sm:mb-6">
//                 <svg
//                   className="w-10 h-10 sm:w-12 sm:h-12 text-primaryColor"
//                   fill="none"
//                   stroke="currentColor"
//                   viewBox="0 0 24 24"
//                 >
//                   <path
//                     strokeLinecap="round"
//                     strokeLinejoin="round"
//                     strokeWidth={2}
//                     d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
//                   />
//                 </svg>
//               </div>
//               <p className="text-secondaryTextColor text-base sm:text-lg">
//                 No certifications available at the moment.
//               </p>
//               <p className="text-secondaryTextColor text-sm mt-2">
//                 We're working on updating our credentials. Please check back
//                 soon.
//               </p>
//             </div>
//           </div>
//         )}
//       </div>
//     </div>
//   );
// };

// export default Certifications;

// import React, { useEffect, useState } from "react";
// import SectionHeading from "./SectionHeading";
// import axiosInstance from "../../../utils/axios";

// const CertificateCard = ({ item }) => {
//   const [isHovered, setIsHovered] = useState(false);

//   return (
//     <div className="max-w-6xl mx-auto">
//       {/* Main Certificate Container */}
//       <div
//         className="relative bg-gradient-to-br from-white via-gray-50 to-primaryFadedColor/5 rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-500 overflow-hidden group border border-primaryFadedColor/20"
//         onMouseEnter={() => setIsHovered(true)}
//         onMouseLeave={() => setIsHovered(false)}
//       >
//         {/* Background Pattern */}
//         <div className="absolute inset-0 opacity-5">
//           <div className="absolute top-0 left-0 w-32 h-32 bg-primaryColor rounded-full -translate-x-16 -translate-y-16"></div>
//           <div className="absolute bottom-0 right-0 w-24 h-24 bg-accentColor rounded-full translate-x-12 translate-y-12"></div>
//           <div className="absolute top-1/2 right-0 w-16 h-16 bg-donatebuttonColor rounded-full translate-x-8"></div>
//         </div>

//         <div className="relative grid md:grid-cols-2 gap-8 p-8 lg:p-12 items-center">
//           {/* Left Side - Certificate Image */}
//           <div className="relative group">
//             <div className="relative">
//               {/* Certificate Frame */}
//               <div
//                 className={`
//                 relative bg-white p-4 rounded-xl shadow-lg border-2 transition-all duration-500
//                 ${
//                   isHovered
//                     ? "border-primaryColor shadow-xl transform rotate-1"
//                     : "border-gray-200 shadow-md"
//                 }
//               `}
//               >
//                 <div className="w-full h-64 lg:h-80 rounded-lg overflow-hidden bg-gray-50">
//                   <img
//                     src={item?.image}
//                     alt="certificate"
//                     className="w-full h-full object-contain transition-transform duration-500 group-hover:scale-105"
//                   />
//                 </div>

//                 {/* Certificate Badge */}
//                 <div className="absolute -top-3 -right-3 bg-gradient-to-r from-primaryColor to-donatebuttonColor text-white p-2 rounded-full shadow-lg">
//                   <svg
//                     className="w-6 h-6"
//                     fill="none"
//                     stroke="currentColor"
//                     viewBox="0 0 24 24"
//                   >
//                     <path
//                       strokeLinecap="round"
//                       strokeLinejoin="round"
//                       strokeWidth={2}
//                       d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
//                     />
//                   </svg>
//                 </div>
//               </div>

//               {/* Floating Elements */}
//               <div className="absolute -bottom-4 -left-4 w-8 h-8 bg-accentColor/20 rounded-full animate-pulse"></div>
//               <div
//                 className="absolute -top-4 -right-8 w-6 h-6 bg-primaryColor/20 rounded-full animate-pulse"
//                 style={{ animationDelay: "0.5s" }}
//               ></div>
//             </div>
//           </div>

//           {/* Right Side - Certificate Details */}
//           <div className="space-y-6">
//             <div className="space-y-3">
//               <h3 className="text-2xl lg:text-3xl font-bold text-primaryTextColor leading-tight">
//                 {item?.title}
//               </h3>
//               <div className="w-full lg:w-24 h-1.5 bg-gradient-to-r from-primaryColor to-accentColor rounded-full"></div>
//             </div>
//           </div>
//         </div>

//         {/* Animated Border on Hover */}
//         <div
//           className={`
//           absolute inset-0 rounded-2xl transition-opacity duration-500 pointer-events-none
//           ${isHovered ? "opacity-100" : "opacity-0"}
//           bg-gradient-to-r from-transparent via-primaryColor/5 to-transparent
//         `}
//         ></div>
//       </div>
//     </div>
//   );
// };

// // Enhanced Skeleton loader
// const CertificateSkeleton = () => {
//   return (
//     <div className="max-w-6xl mx-auto">
//       <div className="bg-white rounded-2xl shadow-lg p-8 lg:p-12 animate-pulse">
//         <div className="grid md:grid-cols-2 gap-8 items-center">
//           {/* Left Side Skeleton */}
//           <div className="space-y-4">
//             <div className="w-full h-64 lg:h-80 bg-skeletonLoaderColor rounded-xl"></div>
//           </div>

//           {/* Right Side Skeleton */}
//           <div className="space-y-6">
//             <div className="w-32 h-6 bg-skeletonLoaderColor rounded-full"></div>
//             <div className="space-y-3">
//               <div className="w-3/4 h-8 bg-skeletonLoaderColor rounded"></div>
//               <div className="w-24 h-2 bg-skeletonLoaderColor rounded-full"></div>
//             </div>
//             <div className="space-y-2">
//               <div className="w-full h-4 bg-skeletonLoaderColor rounded"></div>
//               <div className="w-5/6 h-4 bg-skeletonLoaderColor rounded"></div>
//             </div>
//             <div className="grid grid-cols-2 gap-3">
//               {[...Array(4)].map((_, idx) => (
//                 <div
//                   key={idx}
//                   className="w-3/4 h-4 bg-skeletonLoaderColor rounded"
//                 ></div>
//               ))}
//             </div>
//             <div className="flex gap-4">
//               <div className="w-32 h-12 bg-skeletonLoaderColor rounded-lg"></div>
//               <div className="w-32 h-12 bg-skeletonLoaderColor rounded-lg"></div>
//             </div>
//           </div>
//         </div>
//       </div>

//       {/* Trust Indicators Skeleton */}
//       <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-6">
//         {[...Array(3)].map((_, idx) => (
//           <div key={idx} className="p-6 bg-white/50 rounded-xl animate-pulse">
//             <div className="w-12 h-12 bg-skeletonLoaderColor rounded-full mx-auto mb-3"></div>
//             <div className="w-20 h-4 bg-skeletonLoaderColor rounded mx-auto mb-2"></div>
//             <div className="w-32 h-3 bg-skeletonLoaderColor rounded mx-auto"></div>
//           </div>
//         ))}
//       </div>
//     </div>
//   );
// };

// const Certifications = () => {
//   const [certificates, setCertificates] = useState([]);
//   const [loading, setLoading] = useState(true);

//   const fetchCertificates = async () => {
//     try {
//       const resp = await axiosInstance.get(`/certificate`);
//       setCertificates(resp.data || []);
//     } catch (error) {
//       console.error("Error fetching certificates", error);
//     } finally {
//       setLoading(false);
//     }
//   };

//   useEffect(() => {
//     fetchCertificates();
//   }, []);

//   const featuredCertificate = certificates.slice(0, 1); // only one

//   return (
//     <div className="general-p-m flex flex-col justify-center mx-auto gap-6 lg:gap-16">
//       <SectionHeading
//         subtitle="Certifications"
//         title="Official Listings & Accreditations"
//       />

//       {loading ? (
//         <CertificateSkeleton />
//       ) : featuredCertificate.length > 0 ? (
//         <CertificateCard item={featuredCertificate[0]} />
//       ) : (
//         <div className="text-center py-16">
//           <div className="max-w-md mx-auto">
//             <div className="w-24 h-24 bg-primaryFadedColor/20 rounded-full flex items-center justify-center mx-auto mb-6">
//               <svg
//                 className="w-12 h-12 text-primaryColor"
//                 fill="none"
//                 stroke="currentColor"
//                 viewBox="0 0 24 24"
//               >
//                 <path
//                   strokeLinecap="round"
//                   strokeLinejoin="round"
//                   strokeWidth={2}
//                   d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
//                 />
//               </svg>
//             </div>
//             <p className="text-secondaryTextColor text-lg">
//               No certifications available at the moment.
//             </p>
//             <p className="text-secondaryTextColor text-sm mt-2">
//               We're working on updating our credentials. Please check back soon.
//             </p>
//           </div>
//         </div>
//       )}
//     </div>
//   );
// };

// export default Certifications;
